import { Button } from '@thanx/uikit/button'
import { Text } from '@thanx/uikit/text'
import {
  useGetSandboxPromptQuery,
  useUpdateSandboxPromptMutation,
} from 'api/recoveryAi'
import { buildTranslate } from 'locales'
import React, { useEffect, useRef, useState } from 'react'
import { useStyletron } from 'styletron-react'
import { merchantTheme } from 'theme'
import { TOUR_ANCHORS } from './tourAnchors'

// TODO(accordion-unify): consolidate with src/components/Accordion once it
// supports a card variant with header content + ARIA wiring. Tracked separately.
const t = buildTranslate('recovery_ai.sandbox.sandbox_prompt')

const SandboxPromptAccordion = () => {
  const [css] = useStyletron()
  const bodyId = useRef(
    `sandbox-prompt-body-${Math.random().toString(36).slice(2)}`
  ).current
  const { data } = useGetSandboxPromptQuery()
  const [updateSandboxPrompt, { isLoading }] = useUpdateSandboxPromptMutation()
  const [open, setOpen] = useState(false)
  const [draft, setDraft] = useState('')
  const hasEdited = useRef(false)

  const saved = data?.prompt_text ?? ''
  const dirty = draft !== saved

  // Sync the draft from the server on first load (and on any refetch where the
  // user hasn't typed yet). Once the user has edited the textarea, do not
  // clobber their work — a future refetch (RTK invalidation, refetchOnFocus,
  // etc.) would otherwise silently overwrite in-progress edits.
  useEffect(() => {
    if (!hasEdited.current) setDraft(saved)
  }, [saved])

  return (
    <div
      className={css({
        backgroundColor: 'white',
        border: `1px solid ${merchantTheme.colors.grey20}`,
        borderRadius: '8px',
      })}
    >
      <button
        type="button"
        data-tour={TOUR_ANCHORS.sandboxPrompt}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        aria-controls={bodyId}
        aria-label={open ? t('collapse') : t('expand')}
        className={css({
          width: '100%',
          padding: '12px 16px',
          background: 'none',
          border: 'none',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        })}
      >
        <Text.BodyText3 bold>{t('title')}</Text.BodyText3>
        <i
          className={open ? 'fa fa-caret-down' : 'fa fa-caret-right'}
          aria-hidden="true"
        />
      </button>
      {open && (
        <div
          id={bodyId}
          className={css({
            padding: '0 16px 16px',
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
          })}
        >
          <Text.BodyText4 color="grey70">{t('description')}</Text.BodyText4>
          <Text.BodyText4 color="grey50">
            {t('applies_next_conversation')}
          </Text.BodyText4>
          <textarea
            value={draft}
            onChange={e => {
              hasEdited.current = true
              setDraft(e.target.value)
            }}
            placeholder={t('placeholder')}
            rows={8}
            aria-label={t('title')}
            className={css({
              width: '100%',
              padding: '8px',
              borderRadius: '4px',
              border: `1px solid ${merchantTheme.colors.grey20}`,
              fontFamily: 'inherit',
              fontSize: '14px',
              resize: 'vertical',
            })}
          />
          <div className={css({ alignSelf: 'flex-end' })}>
            <Button
              kind="primary"
              disabled={isLoading || !dirty}
              onClick={() => updateSandboxPrompt(draft)}
            >
              {isLoading ? t('saving') : t('save')}
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export default SandboxPromptAccordion
